import { useCallback } from 'react';
import type {
  SupportMeetingWorkflow,
  SupportNavigationWorkflow,
  SupportWorkflowResume,
  SupportWorkflowResumeRequest,
} from '@web-front/support-chat';

/**
 * Recreates a transient host draft from the persisted AI action. The snapshot
 * is display-only: neither its IDs nor a prior confirmation are executed.
 */
export function useSupportWorkflowResume(
  meetingWorkflow: SupportMeetingWorkflow,
  navigationWorkflow: SupportNavigationWorkflow,
): SupportWorkflowResume {
  return useCallback(
    (request) => resumeSupportWorkflow(request, meetingWorkflow, navigationWorkflow),
    [meetingWorkflow, navigationWorkflow],
  );
}

export async function resumeSupportWorkflow(
  request: SupportWorkflowResumeRequest,
  meetingWorkflow: SupportMeetingWorkflow,
  navigationWorkflow: SupportNavigationWorkflow,
) {
  if (request.action.type === 'prepare_consultation') {
    const refreshed = await meetingWorkflow.prepare(request.action);

    // A stale confirmation must only produce a new, freshly checked preview.
    // It never reaches `confirm` in this resume path.
    if (request.interaction.type !== 'choose' || refreshed.kind !== 'selection') return refreshed;
    return meetingWorkflow.select(refreshed.draftId, request.interaction.choiceId);
  }

  const refreshed = await navigationWorkflow.navigate(request.action);
  if (request.interaction.type !== 'choose' || !refreshed || refreshed.kind === 'info') return refreshed;

  // Both selection and actionable navigation lists carry a new host-only
  // draftId built from current protected API data.
  return navigationWorkflow.select(refreshed.draftId, request.interaction.choiceId);
}
